/**
 * 이상한 방 - 한 판의 최종 튜닝(적용 **순서**의 단일 출처)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 7절(플레이테스트 수치) · `game/tuning.ts` 머리말("재빌드 없이 바뀐다") ·
 *           `game/levels.ts`의 `levelTuning`(순서는 호출부가 정한다) · 로드맵 확정안 3.2절(준비물) · M9 규칙 3
 *
 * ## 순서가 규칙이다: 단계 → 준비물 → URL
 *
 * 세 겹이 같은 필드를 건드릴 수 있어서, 순서가 흩어지면 "이 판의 외우기 시간이 왜 45초인가"에 답할 수 없다.
 *
 *  1. **단계**(`levels.levelTuning`) - 곡선이 정하는 값. 1단계 외우기 30초처럼 기본값을 덮는다.
 *  2. **준비물**(`economy/kits.withKit`) - 그 위에 **더한다**. 배수가 아니라 덧셈이라 어느 단계에서나 같은 크기다.
 *  3. **URL**(`tuning.resolveTuning`) - 마지막에 덮는다. **URL이 준비물을 이긴다.**
 *
 * 3이 마지막인 이유는 `app/urlState.ts`의 표에 적힌 규약 그대로다 - `?changes=1`로 단계를 검사할 수 없으면
 * 그건 도구가 아니다. 준비물이 URL을 이기면 "돋보기를 챙긴 판에서만 `?watch=10`이 안 먹는" 구멍이 생기고,
 * 그 판의 플레이테스트 기록은 통째로 거짓이 된다.
 *
 * ## 매일의 방에는 준비물을 넘길 **자리가 없다**
 *
 * `resolveDailyTuning`에는 준비물 인자가 아예 없다(로드맵 M9 규칙 3). 매일의 방은 같은 시드를 견주는 곳이라
 * 누구는 외우기 75초, 누구는 60초로 돌면 그날의 점수 비교가 성립하지 않는다. 잠그는 코드가 아니라 **타입**으로
 * 막는 이유는 `kits.ts`가 사람 매치에 대해 한 선택과 같다 - 게이트는 잊히지만 없는 인자는 컴파일이 막는다.
 *
 * 여기에 규칙이 새로 생기지 않는다. 세 함수를 정해진 차례로 부르는 것이 이 파일의 전부이고,
 * 값의 규칙은 각각 `levels.ts` · `kits.ts` · `tuning.ts`에 그대로 있다.
 */

import { withKit, type KitDef } from './economy/kits'
import { levelTuning, type LevelDef } from './levels'
import { TUNING, resolveTuning, type Tuning } from './tuning'

/**
 * 한 판의 최종 튜닝. `level`이 `null`이면 자유 모드(단계 없음)라 기본 튜닝 위에서 바로 시작한다.
 * `kit`이 `null`이면 준비물 없이 들어가는 사람이고, 그때 2단계는 참조를 그대로 흘려보낸다(`withKit`).
 */
export function resolveRoundTuning(
  level: LevelDef | null,
  kit: KitDef | null,
  search: string,
  base: Tuning = TUNING,
): Tuning {
  const leveled = level === null ? base : levelTuning(level, base)
  return resolveTuning(search, withKit(leveled, kit))
}

/**
 * 매일의 방의 튜닝. **준비물 인자가 없다**(머리말) - 오늘의 방은 모두에게 같은 규칙이어야 한다.
 * URL 덮어쓰기는 그대로 받는다. 그것은 계정이 산 것이 아니라 개발자의 손잡이이고, 주소를 공유하면
 * 남도 같은 판을 돌 수 있다.
 */
export function resolveDailyTuning(level: LevelDef | null, search: string, base: Tuning = TUNING): Tuning {
  return resolveRoundTuning(level, null, search, base)
}
